





















































































































import {
  defineComponent,
  PropType,
  ref,
  computed,
  watch,
  useContext,
  useRoute,
  useRouter,
} from '@nuxtjs/composition-api';
import { useMediaQuery } from '@vueuse/core';
import ProductCartModalBody from '~/components/pages/Product/ProductCartModalBody.vue';
import ProductVintageModalBody from '~/components/pages/Product/ProductVintageModalBody.vue';
import AppModal from '~/components/partials/App/AppModal.vue';
import type { BgColors, TextColors, SizeTypes, BorderColors } from '~/components/partials/App/AppRectButton.vue';
import AppRectButton from '~/components/partials/App/AppRectButton.vue';
import { useShipmentFreeMessage } from '~/composables/cart/useShipmentFreeMessage';
import { useKarteCartTag } from '~/composables/karte/useKarteCartTag';
import { useGoogleTag } from '~/composables/tag/useGoogleTag';
import { useAlert } from '~/composables/useAlert';
import { useMatchMedia } from '~/composables/useMatchMedia';
import { useStore } from '~/composables/useStore';
import { MERCHANT_ID } from '~/constants';
import { cartNameToProductTypeId, url } from '~/utils';
import { APIError } from '~/utils/error';
import { getSpecialPricePromotion } from '~/utils/modules/products';
import { ProductShowECResponseSchema } from '~/types/modules/product';
import { LotteryApplicationType } from '~/enums';
import EC_ERROR_MESSAGES from '~/locales/api-errors/ec.json';

export default defineComponent({
  components: {
    AppModal,
    AppRectButton,
    ProductVintageModalBody,
    ProductCartModalBody,
  },
  props: {
    lottery: {
      type: Object as PropType<{
        applicationStatus: LotteryApplicationType;
        id?: number;
        text?: string;
        height?: string;
        raffle_control_start_time?: string;
        raffle_control_end_time?: string;
      }>,
      default: undefined,
    },
    bgColor: {
      type: String as PropType<BgColors>,
      default: 'red',
    },
    height: {
      type: String as PropType<SizeTypes>,
      default: 'h-10',
    },
    hasStock: {
      type: Boolean,
      default: false,
    },
    isNowOnSale: {
      type: Boolean,
      default: false,
    },
    singleProductVariantId: {
      type: Number,
      default: null,
    },
    ecResponse: {
      type: Object as PropType<ProductShowECResponseSchema>,
      default: null,
    },
    productCode: {
      type: String,
      default: null,
    },
    variantCodes: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    lotteryItemId: {
      type: String,
      default: null,
    },
    pageType: {
      type: String,
      default: null,
    },
    isDisplay: {
      type: Boolean,
      default: true,
    },
    isCart: {
      type: Boolean,
      default: false,
    },
    borderColor: {
      type: String as PropType<BorderColors>,
      default: undefined,
    },
  },
  setup(props) {
    const { i18n } = useContext();
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const karte = useKarteCartTag();
    const { setGoogleCartTag } = useGoogleTag();
    const { isMobile } = useMatchMedia();
    const { openAlert, closeAlert } = useAlert();
    const isShownVintageModal = ref(false);
    const localEcResponse = ref<ProductShowECResponseSchema>(props.ecResponse);
    const selectedProductVariantId = ref(0);
    const hasPurchased = ref(false);
    const isAppliedLottery = ref(false);
    const isClickedLottery = ref(false);
    const isUpdatingCart = ref(false);
    const isTouchDevice = useMediaQuery('(hover: none)');
    const isCartProcessing = computed(() => store.state.modules.cart.isCartProcessing);
    const productVariants = computed(() => {
      if (!localEcResponse.value) return [];
      if (props.variantCodes.length > 0) {
        return localEcResponse.value.product_variants.filter((variant) => props.variantCodes.includes(variant.code));
      }
      return localEcResponse.value.product_variants;
    });
    const textColor = computed((): TextColors => (props.bgColor === 'red' ? 'white' : 'red'));
    const authenticated = computed(() => store.state.auth.authenticated);
    const { setShippingAndPayment } = useShipmentFreeMessage();

    const cartItemQuantity = computed(() => {
      for (const cart of store.state.modules.cart.carts) {
        const productTypeId = cartNameToProductTypeId(cart.cart_name);
        const item = cart.items.find(
          ({ product_variant_id: variantId }) => variantId === selectedProductVariantId.value
        );

        // 商品が不正なカートに入っている（一般商品がボジョレーカートに入っているなど）場合を回避する
        if (item && item.product_type_id === productTypeId) {
          return item.quantity;
        }
      }

      return 0;
    });

    const openLotteryConfirmationAlert = async (lotteryId?: number) => {
      const isLotteryDetailPage = route.value.path === url('LOTTERY_DETAIL', { id: lotteryId });
      if (!isLotteryDetailPage) {
        await router.push({ path: url('LOTTERY_DETAIL', { id: lotteryId }) });
        return;
      }

      if (!authenticated.value) {
        openAlert({
          title: i18n.t('Other-37.applicationButton.error').toString(),
          buttons: [
            {
              text: 'キャンセル',
              style: 'cancel',
            },
            {
              text: 'ログイン',
              style: 'submit',
              async onClick() {
                try {
                  await router.push({ path: url('AUTH_LOGIN') });
                } catch {
                  return false;
                }
              },
            },
          ],
        });
        return;
      }

      const alert = openAlert({
        title: i18n.t('Item-03.button.lotteryConfirmationMessage').toString(),
        buttons: [
          {
            text: i18n.t('Item-03.button.cancelLottery').toString(),
            style: 'cancel',
          },
          {
            text: i18n.t('Item-03.button.proceedToLottery').toString(),
            style: 'submit',
            async onClick() {
              // 多重クリック防止のため、2回目以降のクリックを非活性にする
              if (isClickedLottery.value) return false;
              try {
                isClickedLottery.value = true;
                await onClickLottery(lotteryId);
              } catch (error) {
                // アラートを閉じないとエラーアラートが後続に残ってしまうため
                closeAlert(alert.id);
                throw error;
              } finally {
                isClickedLottery.value = false;
              }
            },
          },
        ],
      });
    };

    const onClickLottery = async (lotteryId?: number) => {
      if (!isShownVintageModal.value && productVariants.value.length > 1 && !props.singleProductVariantId) {
        isShownVintageModal.value = true;
        return;
      }

      if (!lotteryId || !props.lotteryItemId) {
        openErrorDialog(i18n.t('Item-02.cart.alert.lottery').toString());
        return;
      }

      // lotteryItemIdがあれば応募する(現状lotteryItemIdを設定しているのは抽選詳細のみ)
      await store.dispatch('modules/lottery/postLotteryApplication', {
        body: { raffle_item_id: +props.lotteryItemId },
      });
      isAppliedLottery.value = true;
    };

    const closeLotteryModal = () => {
      isAppliedLottery.value = false;
      isUpdatingCart.value = false;
      isShownVintageModal.value = false;
      store.dispatch('modules/cart/setIsCartProcessing', false);
    };

    const openErrorDialog = (title?: string | undefined, hideMsg = false) => {
      openAlert({
        title: title || i18n.t('Item-02.cart.alert.title').toString(),
        message: !hideMsg ? i18n.t('Item-02.cart.alert.message').toString() : undefined,
        buttons: [
          {
            text: i18n.t('Item-02.cart.alert.back').toString(),
            style: 'submit',
          },
        ],
      });
      // エラーになった場合もカートボタンの非活性を解除する
      isUpdatingCart.value = false;
      store.dispatch('modules/cart/setIsCartProcessing', false);
    };

    const addToCart = async (productId?: number) => {
      // 多重クリック防止のため、全てのボタンを非活性にする
      await store.dispatch('modules/cart/setIsCartProcessing', true);
      if (!localEcResponse.value) {
        try {
          localEcResponse.value = await store.dispatch('product/detail/fetchECProductDetail', {
            path: { code: props.productCode },
          });
        } catch {
          openErrorDialog();
        }
      }

      if (!isShownVintageModal.value && productVariants.value.length > 1 && !props.singleProductVariantId) {
        isShownVintageModal.value = true;
        await store.dispatch('modules/cart/setIsCartProcessing', false);
        return;
      }

      if (props.isCart) {
        store.commit('modules/cart/setIsFromCartRecommend', true);
      }
      selectedProductVariantId.value = productId || props.singleProductVariantId || productVariants.value[0].id;
      try {
        if (localEcResponse.value.set_type) {
          await store.dispatch('modules/cart/updateCart', {
            setProducts: [{ product_variant_id: selectedProductVariantId.value, quantity: 1 }],
          });
        } else {
          await store.dispatch('modules/cart/updateCart', {
            singleProducts: [
              {
                product_variant_id: selectedProductVariantId.value,
                quantity: cartItemQuantity.value + 1,
              },
            ],
          });
        }

        const { path, fullPath } = route.value;

        const productVariant =
          productVariants.value.find(({ id }) => id === selectedProductVariantId.value) || productVariants.value[0];
        const specialPrice = getSpecialPricePromotion(productVariant.price_settings);
        const taxIncludedPrice = specialPrice
          ? specialPrice.price_including_tax
          : productVariant.price_settings?.price_including_tax;
        const taxExcludedPrice = specialPrice
          ? specialPrice.price_excluding_tax
          : productVariant.price_settings?.price_excluding_tax;
        const { langs, categories } = localEcResponse.value;

        /** eコマース用カートインイベントの発火 */
        setGoogleCartTag(
          taxExcludedPrice,
          {
            item_name: langs.find(({ id }) => id === 'ja')?.name,
            item_id: localEcResponse.value.code,
            price: taxIncludedPrice,
            item_category: categories[0].name,
            item_list_name: path === url('ITEM_LIST') ? fullPath : path,
          },
          props.pageType
        );

        /* GTMにタグ設置のイベントを発火 */
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'tlb_dmp_cart',
          tlb_dmp: { user_id: store.state.user.info.userBase?.uuid, item_id: selectedProductVariantId.value },
        });

        /**
         * カートイン後のモーダルに出すメッセージを取得する
         */
        await setShippingAndPayment(localEcResponse.value.type);
      } catch (e) {
        store.commit('modules/cart/setIsFromCartRecommend', false);

        if (!APIError.isAPIError(e)) {
          throw e;
        }

        const errorMessage = Object.entries(EC_ERROR_MESSAGES).find(
          ([code]) => APIError.isAPIError(e) && e.hasCode(code)
        );

        // よりどり商品のアラート、抽選商品の重複カートインエラー、販売期間外エラーに「再度お試し」の表示は不要
        const hideMsg =
          errorMessage &&
          [
            'ec_product_set_item_quantity_must_equal_with_setting',
            'ec_raffle_can_purchase_only_one',
            'ec_raffle_has_been_purchased',
            'ec_raffle_not_purchase_period',
            'ec_product_variant_is_not_sale',
          ].includes(errorMessage[0]);

        openErrorDialog(errorMessage?.[1], hideMsg);
        return;
      }
      // モーダル閉じる
      isShownVintageModal.value = false;
      // ポップアップ表示＆カートボタン表示切り替え
      hasPurchased.value = true;
      isUpdatingCart.value = false;
      // カートイン完了時にカートボタンの非活性を解除する
      store.dispatch('modules/cart/setIsCartProcessing', false);

      // ↓から外部連携用の処理
      // スタッフスタート カート計測
      if (window.staffStart) {
        try {
          const skuCode =
            localEcResponse.value.product_variants.find(({ id }) => id === selectedProductVariantId.value)
              ?.enterprise_system_code || '';
          window.staffStart.cart.sendTrackingRequest(MERCHANT_ID, {
            product_code: skuCode,
            count: 1,
          });
        } catch {
          // 何もしない
        }
      }
      // KARTE
      await karte.setKarteCartTag();
      // collect_code
      try {
        if (window._etmc) {
          window._etmc.push(['trackPageView', { item: 'c_' + localEcResponse.value.code }]);
        }
      } catch {
        // 何もしない
      }
    };
    const updateCart = () => {
      isUpdatingCart.value = true;
      addToCart();
    };

    const handleVintageModalClose = () => {
      isShownVintageModal.value = false;
      isUpdatingCart.value = false;
      // ヴィンテージモーダルクローズ時にカートボタンの非活性も解除する
      store.dispatch('modules/cart/setIsCartProcessing', false);
    };

    const isFullSizeDisplay = ref(store.state.modules.cart.isFullSizeDisplay);

    const handlePurchasedModalClose = () => {
      hasPurchased.value = false;

      // カート画面かつ0件の時であれば画面表示を切り替える
      if (props.isCart && isFullSizeDisplay.value) {
        store.commit('modules/cart/setFullSizeDisplayFlag', false);
      }
    };

    watch(
      () => props.ecResponse,
      () => {
        localEcResponse.value = props.ecResponse;
      }
    );

    // モーダル閉じたタイミングでフラグを更新（カート画面のシルバーエッグ枠用）
    watch(
      () => hasPurchased.value,
      (newVal, oldVal) => {
        if (url('CART') && newVal === false && oldVal === true) {
          store.commit('modules/cart/setHasClosedModal', true);
        }
      }
    );

    return {
      openLotteryConfirmationAlert,
      onClickLottery,
      isShownVintageModal,
      hasPurchased,
      isAppliedLottery,
      isUpdatingCart,
      localEcResponse,
      productVariants,
      isTouchDevice,
      textColor,
      addToCart,
      updateCart,
      handleVintageModalClose,
      handlePurchasedModalClose,
      isMobile,
      LotteryApplicationType,
      isCartProcessing,
      closeLotteryModal,
    };
  },
});
